import { Participant, Room, Track } from 'livekit-client'

type Meter = {
  source: MediaStreamAudioSourceNode
  analyser: AnalyserNode
  samples: Uint8Array
  noiseFloor: number
}

export class RemoteAudioMeter {
  private readonly context = new AudioContext({ latencyHint: 'interactive' })
  private readonly meters = new Map<string, Meter>()
  private timer: number | null = null
  private smoothLevel = 0
  private speakingUntil = 0

  constructor(
    private readonly room: Room,
    private readonly isRemote: (participant: Participant) => boolean,
    private readonly onActivity: (speaking: boolean, level: number) => void
  ) {}

  start() {
    if (this.timer !== null) return
    document.addEventListener('pointerdown', this.resume, { passive: true })
    document.addEventListener('visibilitychange', this.resume)
    this.resume()
    this.timer = window.setInterval(this.measure, 32)
  }

  stop() {
    if (this.timer !== null) window.clearInterval(this.timer)
    this.timer = null
    document.removeEventListener('pointerdown', this.resume)
    document.removeEventListener('visibilitychange', this.resume)
    this.meters.forEach(({ source }) => source.disconnect())
    this.meters.clear()
    this.onActivity(false, 0)
    void this.context.close().catch(() => undefined)
  }

  private readonly resume = () => {
    void this.context.resume().catch(() => undefined)
  }

  private readonly measure = () => {
    const participants = Array.from(this.room.remoteParticipants.values()).filter(this.isRemote)
    const activeTrackIds = new Set<string>()
    let detectedSpeech = false
    const rawLevel = participants.reduce((maximum, participant) => {
      const publication =
        participant.getTrackPublication(Track.Source.Microphone) ||
        Array.from(participant.audioTrackPublications.values())[0]
      const mediaTrack = publication?.track?.mediaStreamTrack
      if (mediaTrack) activeTrackIds.add(mediaTrack.id)
      const measured = this.trackLevel(mediaTrack)
      detectedSpeech = detectedSpeech || participant.isSpeaking || measured.speaking
      return Math.max(maximum, participant.audioLevel || 0, measured.level)
    }, 0)
    this.meters.forEach(({ source }, trackId) => {
      if (activeTrackIds.has(trackId)) return
      source.disconnect()
      this.meters.delete(trackId)
    })
    this.smoothLevel += (rawLevel - this.smoothLevel) * (rawLevel > this.smoothLevel ? 0.62 : 0.12)
    if (this.smoothLevel < 0.001) this.smoothLevel = 0
    if (detectedSpeech) this.speakingUntil = Date.now() + 220
    this.onActivity(Date.now() < this.speakingUntil, this.smoothLevel)
  }

  private trackLevel(track?: MediaStreamTrack) {
    if (!track || track.readyState !== 'live') return { level: 0, speaking: false }
    let meter = this.meters.get(track.id)
    if (!meter) {
      try {
        const source = this.context.createMediaStreamSource(new MediaStream([track]))
        const analyser = this.context.createAnalyser()
        analyser.fftSize = 512
        analyser.smoothingTimeConstant = 0.2
        source.connect(analyser)
        meter = { source, analyser, samples: new Uint8Array(analyser.fftSize), noiseFloor: 0.003 }
        this.meters.set(track.id, meter)
      } catch {
        return { level: 0, speaking: false }
      }
    }
    meter.analyser.getByteTimeDomainData(meter.samples)
    let total = 0
    for (const value of meter.samples) {
      const normalized = (value - 128) / 128
      total += normalized * normalized
    }
    const level = Math.sqrt(total / meter.samples.length)
    if (level < meter.noiseFloor * 1.6) meter.noiseFloor += (level - meter.noiseFloor) * 0.035
    const threshold = Math.min(0.028, Math.max(0.008, meter.noiseFloor * 2.4))
    return { level, speaking: level >= threshold }
  }
}
